import { createMotionTransition, designLayers } from './interaction';
import { Box, Drawer } from '@mui/material';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { SectionSidebar, sectionSidebarWidth } from './SectionSidebar';
import { WorkspaceHeader } from './WorkspaceHeader';
import { PageLayout } from './PageLayout';
import { PageHeaderActionsProvider } from './patterns/PageHeaderActionsContext';
import type { AccountDescriptor, NavigationModeAction, SectionNavigationGroup, SessionContextDescriptor, WorkspaceTaskDescriptor } from './navigation';
import { designTokens } from './tokens';

const desktopNavigationStorageKey = 'app-shell.desktop-navigation-expanded';

function storedDesktopNavigationExpanded() {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(desktopNavigationStorageKey) !== 'false';
  } catch {
    return true;
  }
}

function storeDesktopNavigationExpanded(expanded: boolean) {
  try {
    window.localStorage.setItem(desktopNavigationStorageKey, String(expanded));
  } catch {
    // The shell still works when browser storage is unavailable.
  }
}

export type AppShellProps<PageID extends string> = {
  brandLabel: string;
  brandLogoUrl?: string;
  brandLogoLabel?: string;
  brandLogoLoading?: boolean;
  groups: readonly SectionNavigationGroup<PageID>[];
  currentPage: PageID;
  pageLabel: string;
  pageVariant?: 'default' | 'overview';
  account: AccountDescriptor;
  sessionContext?: SessionContextDescriptor;
  navigationMode: 'employee' | 'admin';
  navigationLabel: string;
  navigationModeAction?: NavigationModeAction;
  onBrandClick: () => void;
  /** false를 반환하면 현재 메뉴와 초점을 유지합니다. */
  onNavigate: (page: PageID) => boolean | void;
  tasks?: WorkspaceTaskDescriptor;
  search?: ReactNode;
  immersive?: boolean;
  /** app이 명시적인 새 화면 이동 때만 변경합니다. 목록 내 상세·뒤로 가기는 기존 포커스를 보존합니다. */
  contentFocusKey?: number;
  children: ReactNode;
};

export function AppShell<PageID extends string>({
  brandLabel,
  brandLogoUrl,
  brandLogoLabel,
  brandLogoLoading = false,
  groups,
  currentPage,
  pageLabel,
  pageVariant = 'default',
  account,
  sessionContext,
  navigationMode,
  navigationLabel,
  navigationModeAction,
  onBrandClick,
  onNavigate,
  tasks,
  search,
  immersive = false,
  contentFocusKey,
  children,
}: AppShellProps<PageID>) {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [navigatingFromDrawer, setNavigatingFromDrawer] = useState(false);
  const mainId = useId();
  const mainRef = useRef<HTMLElement>(null);
  const previousFocusKey = useRef(contentFocusKey);
  const [desktopNavigationExpanded, setDesktopNavigationExpanded] = useState(storedDesktopNavigationExpanded);
  const overview = pageVariant === 'overview';
  const navigate = (page: PageID) => {
    if (onNavigate(page) === false) return;
    setNavigatingFromDrawer(navigationOpen && page !== currentPage);
    setNavigationOpen(false);
  };
  const toggleNavigation = () => setDesktopNavigationExpanded((expanded) => !expanded);
  const desktopNavigationWidth = sectionSidebarWidth;
  const modeAction = navigationModeAction ? {
    ...navigationModeAction,
    onSelect: () => {
      if (navigationModeAction.onSelect() === false) return;
      setNavigatingFromDrawer(navigationOpen);
      setNavigationOpen(false);
    },
  } : undefined;

  useEffect(() => {
    storeDesktopNavigationExpanded(desktopNavigationExpanded);
  }, [desktopNavigationExpanded]);

  useEffect(() => {
    if (previousFocusKey.current === contentFocusKey) return;
    previousFocusKey.current = contentFocusKey;
    if (contentFocusKey !== undefined) mainRef.current?.focus({ preventScroll: true });
  }, [contentFocusKey]);

  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100vh',
    '@supports (height: 100dvh)': { height: '100dvh' },
    minHeight: 0,
    overflow: 'hidden',
    bgcolor: overview ? designTokens.color.overviewSurface : designTokens.color.canvas,
    color: designTokens.color.primaryText,
  }}>
    <Box component="a" href={`#${mainId}`} onClick={(event) => { event.preventDefault(); mainRef.current?.focus({ preventScroll: true }); }} sx={{
      position: 'fixed', top: 8, left: 8, transform: 'translateY(calc(-100% - 16px))',
      zIndex: designLayers.skipLink, bgcolor: designTokens.color.canvas, color: designTokens.color.action,
      px: 2, py: 1.5, borderRadius: designTokens.radius.button, boxShadow: designTokens.shadow.floatingControl,
      '&:focus': { transform: 'translateY(0)', outline: `3px solid ${designTokens.overlay.focusStrong}`, outlineOffset: 2 },
    }}>본문 바로가기</Box>
    <Box aria-hidden={immersive} inert={immersive ? true : undefined} sx={{ display: immersive ? 'none' : 'block' }}>
      <WorkspaceHeader brandLabel={brandLabel} brandLogoUrl={brandLogoUrl} brandLogoLabel={brandLogoLabel} brandLogoLoading={brandLogoLoading} pageLabel={pageLabel} account={account} sessionContext={sessionContext} navigationExpanded={desktopNavigationExpanded} onBrandClick={onBrandClick} onOpenNavigation={() => { setNavigatingFromDrawer(false); setNavigationOpen(true); }} onToggleNavigation={toggleNavigation} tasks={tasks} search={search} />
    </Box>
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
      <Box
        data-testid="desktop-navigation"
        aria-hidden={immersive || !desktopNavigationExpanded}
        inert={immersive || !desktopNavigationExpanded ? true : undefined}
        sx={{
          display: 'none',
          width: desktopNavigationExpanded ? desktopNavigationWidth : 0,
          height: '100%',
          flex: '0 0 auto',
          overflow: 'hidden',
          pointerEvents: desktopNavigationExpanded ? 'auto' : 'none',
          transition: createMotionTransition('width', 'expand'),
          willChange: 'width',
          '@media (min-width: 1201px)': { display: immersive ? 'none' : 'block' },
        }}
      >
        <Box sx={{ display: 'flex', width: desktopNavigationWidth, height: '100%' }}>
          <SectionSidebar ariaLabel={navigationLabel} groups={groups} currentPage={currentPage} onNavigate={navigate} mode={navigationMode} footerAction={modeAction} />
        </Box>
      </Box>
      <Box component="main" ref={mainRef} id={mainId} tabIndex={-1} aria-label={pageLabel} sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', outline: 'none', bgcolor: overview ? designTokens.color.overviewSurface : designTokens.color.canvas }}>
        <PageHeaderActionsProvider presentation={navigationMode === 'admin' ? 'menu' : 'inline'}>
          <PageLayout overview={overview}>{children}</PageLayout>
        </PageHeaderActionsProvider>
      </Box>
    </Box>
    <Drawer anchor="left" open={!immersive && navigationOpen} disableRestoreFocus={navigatingFromDrawer} onClose={() => setNavigationOpen(false)} slotProps={{ paper: { sx: { top: `${designTokens.layout.workspaceHeaderHeight}px`, height: `calc(100% - ${designTokens.layout.workspaceHeaderHeight}px)`, width: sectionSidebarWidth, maxWidth: 'calc(100vw - 40px)', overflow: 'hidden', bgcolor: designTokens.color.overviewSurface } }, backdrop: { sx: { top: `${designTokens.layout.workspaceHeaderHeight}px`, bgcolor: designTokens.overlay.navigationBackdrop } } }}>
      <SectionSidebar compact ariaLabel={navigationLabel} groups={groups} currentPage={currentPage} onNavigate={navigate} mode={navigationMode} footerAction={modeAction} />
    </Drawer>
  </Box>;
}
